import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Add as AddIcon,
  StorefrontOutlined as StorefrontIcon,
  Label as LabelIcon,
  ViewWeek as ViewWeekIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material'
import { DataGrid } from '@mui/x-data-grid'
import { useNavigate, useOutletContext } from 'react-router-dom'
import AddLeadDialog from '../components/dialogs/AddLeadDialog'
import { AddCustomLabelDialog } from '../components/leads/status'
import { ManageLeadColumnsDialog } from '../components/dialogs/ManageLeadColumnsDialog'
import { LeadColumnMenu } from '../components/dialogs/LeadColumnMenu'
import { useEditLeadDialog } from '../hooks/leads/useEditLeadDialog'
import { useLeads } from '../hooks/leads/useLeads'
import { useLeadStatuses } from '../hooks/leads/useLeadStatuses'
import { useLeadColumns } from '../hooks/leads/useLeadColumns'
import { useLeadGridColumns } from '../hooks/leads/useLeadGridColumns'
import { useNotification } from '../contexts/NotificationContext'
import SMBSelector from "../components/SMBSelector";
import ConversationDetailDialog from '../components/dialogs/ConversationDetailDialog'
import { leadsApi } from '../services/leadsApi'
import { parseCustomFields } from '../utils/leads/leadsUtils'

import { useSMBContext } from '../contexts/SMBContext'

import PageBanner from '../components/PageBanner'
import EmptyState from '../components/EmptyState'
import { TableSkeleton } from '../components/skeletons'
import { PAGE_BANNER_PRIMARY_BTN_SX } from '../theme/constants'

interface Lead {
  id: string
  fullName: string
  firstName: string
  lastName: string
  phone: string
  status: string
  source: string
  // qualificationScore?: number
  createdAt: Date
  lastActivity?: Date
  conversationId?: string | null
  notes?: string
  custom_fields?: Record<string, any>
}

const Leads: React.FC = () => {
  const navigate = useNavigate();
  const outletContext = useOutletContext<{ smbNavState?: { isOnboarded: boolean; hasSubscription: boolean } } | null>()
  const hasSubscription = outletContext?.smbNavState?.hasSubscription
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { showNotification } = useNotification()
  const { smbs, selectedSMBId, setSelectedSMBId, loading: smbsLoading } = useSMBContext()
  const [addLeadOpen, setAddLeadOpen] = useState(false)
  const [addCustomLabelOpen, setAddCustomLabelOpen] = useState(false)
  const [manageColumnsOpen, setManageColumnsOpen] = useState(false)
  const [manageColumnsTab, setManageColumnsTab] = useState<number>(0)
  const [statusUpdateTargetLeadId, setStatusUpdateTargetLeadId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const [leads, setLeads] = useState<Lead[]>([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [totalLeads, setTotalLeads] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [conversationDialogOpen, setConversationDialogOpen] = useState(false)
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null)

  // Use dynamic column resolution hook
  const {
    customColumns,
    resolvedColumns,
    visibleColumns,
    createCustomColumn,
    deactivateCustomColumn,
    addOptionToColumn,
    patchCustomColumn,
    saveTableConfig,
    hideColumn,
  } = useLeadColumns(selectedSMBId);

  const handleOpenManageColumns = (initialTab = 0) => {
    setManageColumnsTab(initialTab);
    setManageColumnsOpen(true);
  };

  const handleHideColumnFromGrid = async (colKey: string) => {
    try {
      const colToHide = resolvedColumns.find((c) => c.key === colKey);
      const label = colToHide?.label || colKey;
      await hideColumn(colKey);
      showNotification(`"${label}" column has been hidden. You can unhide it anytime from Manage Columns.`, 'info');
    } catch (err: any) {
      showNotification(err.message || 'Failed to hide column', 'error');
    }
  };

  const fetchLeads = async (isRefresh: boolean = true) => {
    if (!selectedSMBId) {
      setLeads([]);
      setLoading(false);
      return;
    }

    try {
      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }

      const data = await leadsApi.getLeads({
        smbId: selectedSMBId,
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        search: searchQuery || undefined,
      });

      setLeads(
        (data.leads || []).map((lead: any) => ({
          id: lead.id,
          fullName: lead.name,
          firstName: lead.name?.split(" ")[0] || "",
          lastName: lead.name?.split(" ").slice(1).join(" ") || "",
          phone: lead.phone,
          status: lead.lead_status,
          source: lead.source,
          createdAt: new Date(lead.created_at),
          lastActivity: lead.updated_at ? new Date(lead.updated_at) : undefined,
          conversationId: lead.conversation_id || null,
          notes: lead.notes || lead.lead_metadata?.notes || "",
          custom_fields: parseCustomFields(lead.custom_fields || lead.lead_metadata?.custom_fields),
        }))
      );
      setTotalLeads(data.total || 0);
    } catch (err: any) {
      console.error(err);
      showNotification("Failed to fetch leads", "error");
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
      setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchLeads(false);
  }, [selectedSMBId, paginationModel, searchQuery]);

  const handleMessage = (conversationId: string | null) => {
    if (!conversationId) {
      showNotification("No conversation started yet.", "info");
      return;
    }
    setSelectedConversationId(conversationId)
    setConversationDialogOpen(true)
  }

  const handleCloseConversationDialog = () => {
    setConversationDialogOpen(false);
    setSelectedConversationId(null);
  };

  const handleView = (row: any) => {
    openEditLead(row.id, row.fullName, row.phone, row.source, row.status, 'view', undefined, row.notes, row.custom_fields);
  }

  const handleEditLead = (row: any) => {
    openEditLead(row.id, row.fullName, row.phone, row.source, row.status, 'edit', undefined, row.notes, row.custom_fields);
  }

  const {
    handleStatusChange,
    handleSourceChange,
    handleCustomFieldChange,
  } = useLeads({
    smbId: selectedSMBId || undefined,
    setLeads,
    fetchLeads,
  });

  const {
    statuses: customStatuses,
    setStatuses: setCustomStatuses,
    fetchStatuses,
    handleStatusComplete,
  } = useLeadStatuses(selectedSMBId);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  const { openEditLead, renderEditLeadDialog } = useEditLeadDialog({
    smbId: selectedSMBId,
    customColumns,
    onSuccess: (updatedLead) => {
      if (updatedLead) {
        setLeads(prevLeads =>
          prevLeads.map(lead =>
            lead.id === updatedLead.id
              ? {
                  ...lead,
                  fullName: updatedLead.name,
                  firstName: updatedLead.name?.split(" ")[0] || "",
                  lastName: updatedLead.name?.split(" ").slice(1).join(" ") || "",
                  phone: updatedLead.phone,
                  status: updatedLead.lead_status || lead.status,
                  source: updatedLead.source || lead.source,
                  notes: updatedLead.notes,
                  custom_fields: parseCustomFields(updatedLead.custom_fields),
                }
              : lead
          )
        )
      } else {
        fetchLeads();
      }
    },
    onStatusUpdated: handleStatusComplete,
    customStatuses,
    setCustomStatuses,
  });

  // Dynamically build DataGrid columns based on resolved visible columns
  const gridColumns = useLeadGridColumns({
    visibleColumns,
    customColumns,
    customStatuses,
    handleStatusChange,
    handleSourceChange,
    handleCustomFieldChange,
    onAddOptionToColumn: addOptionToColumn,
    handleView,
    handleEditLead,
    handleMessage,
    onAddCustomLabelClick: (targetLeadId) => {
      setStatusUpdateTargetLeadId(targetLeadId);
      setAddCustomLabelOpen(true);
    },
    leads,
  });

  const handleAddLeadComplete = () => {
    setAddLeadOpen(false)
    fetchLeads()
  }

  const handleCustomLabelComplete = async (
    res?: { default?: string[]; custom?: string[] },
    createdStatus?: string
  ) => {
    await handleStatusComplete(res, createdStatus, undefined, undefined, setCustomStatuses);

    if (res && statusUpdateTargetLeadId && createdStatus) {
      setLeads((prevLeads) =>
        prevLeads.map((lead) =>
          lead.id === statusUpdateTargetLeadId
            ? { ...lead, status: createdStatus as any }
            : lead
        )
      );
      try {
        await leadsApi.updateLead(statusUpdateTargetLeadId, { lead_status: createdStatus });
        showNotification("Lead status updated to the new status", "success");
      } catch (err) {
        console.error("Failed to auto-update lead status:", err);
        showNotification("Failed to update lead status", "error");
      }
    }
    fetchLeads();
    setStatusUpdateTargetLeadId(null);
  };

  return (
    <Box>
      {/* Page Header */}
      <PageBanner
        title="Leads"
        subtitle="Manage and track your leads"
        action={
          <>
            <Button
              variant="contained"
              startIcon={<ViewWeekIcon />}
              onClick={() => handleOpenManageColumns(0)}
              disabled={loading || smbsLoading || !selectedSMBId}
              fullWidth={isMobile}
              sx={PAGE_BANNER_PRIMARY_BTN_SX}
            >
              Manage Columns
            </Button>
            <Button
              variant="contained"
              startIcon={<LabelIcon />}
              onClick={() => setAddCustomLabelOpen(true)}
              disabled={loading || smbsLoading || !selectedSMBId}
              fullWidth={isMobile}
              sx={PAGE_BANNER_PRIMARY_BTN_SX}
            >
              Manage Status
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddLeadOpen(true)}
              disabled={loading || smbsLoading || !selectedSMBId}
              fullWidth={isMobile}
              sx={PAGE_BANNER_PRIMARY_BTN_SX}
            >
              Add Lead
            </Button>
          </>
        }
        onRefresh={() => fetchLeads()}
        refreshing={isRefreshing}
      />

      {/* SMB Selection */}
      <SMBSelector
        smbs={smbs}
        selectedSMBId={selectedSMBId}
        loading={smbsLoading}
        onChange={(value) => {
          setSelectedSMBId(value);
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
          setSearchInput("");
          setSearchQuery("");
        }}
      />

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Stack direction={isMobile ? "column" : "row"} spacing={2} alignItems={isMobile ? "stretch" : "center"}>
            <TextField
              placeholder="Search by name or phone..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: searchInput ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="clear search"
                      onClick={() => setSearchInput('')}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
                sx: { height: 40, borderRadius: 1.5 }
              }}
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ height: 600, width: '100%' }}>
            {(smbs.length === 0 || (smbs.length === 1 && !smbs[0].onboarding_completed) || (selectedSMBId && !smbs.find((s) => s.id === selectedSMBId)?.onboarding_completed)) && !smbsLoading ? (
              hasSubscription === false ? (
                <EmptyState
                  icon={<StorefrontIcon />}
                  title="Subscription Required"
                  description="An active plan is required to access Leads. Choose a plan to start managing your leads."
                  action={
                    <Button variant="contained" onClick={() => navigate('/dashboard')}>
                      Choose Plan
                    </Button>
                  }
                  minHeight={400}
                />
              ) : (
                <EmptyState
                  icon={<StorefrontIcon />}
                  title="Add a Business to Get Started"
                  description="You haven't added a business yet. Connect your WhatsApp business to start capturing leads."
                  action={
                    <Button variant="contained" onClick={() => navigate('/dashboard')}>
                      Add Business
                    </Button>
                  }
                  minHeight={400}
                />
              )
            ) : !selectedSMBId && !smbsLoading ? (
              <EmptyState
                icon={<StorefrontIcon />}
                title="No Business Selected"
                description="Select a business from the dropdown above to view leads."
                minHeight={400}
              />
            ) : visibleColumns.length === 0 && !loading && !smbsLoading ? (
              <EmptyState
                icon={<ViewWeekIcon />}
                title="No columns visible"
                description="Enable at least one column to view lead data."
                action={
                  <Button
                    variant="contained"
                    startIcon={<ViewWeekIcon />}
                    onClick={() => handleOpenManageColumns(0)}
                    size="small"
                  >
                    Manage Columns
                  </Button>
                }
                minHeight={400}
              />
            ) : (
              <DataGrid
                rows={leads}
                columns={gridColumns}
                loading={(loading && !isRefreshing) || smbsLoading}
                rowCount={totalLeads}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50]}
                // checkboxSelection
                disableRowSelectionOnClick
                localeText={{
                  filterValueAny: 'All',
                }}
                slots={{
                  loadingOverlay: () => <TableSkeleton variant="leads" />,
                  columnMenu: LeadColumnMenu,
                  noRowsOverlay: () => (
                    <EmptyState
                      title={searchQuery ? "No matching leads found" : "No leads captured yet"}
                      description={
                        searchQuery
                          ? "No leads match your active search query. Try adjusting your search."
                          : "Leads added manually or captured automatically from customer conversation will appear here."
                      }
                      action={
                        !searchQuery ? (
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setAddLeadOpen(true)}
                            size="small"
                          >
                            Add Lead
                          </Button>
                        ) : undefined
                      }
                      minHeight={400}
                    />
                  ),
                }}
                slotProps={{
                  columnMenu: {
                    onHideColumn: handleHideColumnFromGrid,
                    onOpenManageColumns: handleOpenManageColumns,
                  } as any,
                }}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid rgba(224, 224, 224, 1)',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f9fafb',
                    borderBottom: '2px solid rgba(224, 224, 224, 1)',
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <AddLeadDialog
        open={addLeadOpen}
        onClose={() => setAddLeadOpen(false)}
        onComplete={handleAddLeadComplete}
        onStatusUpdated={() => fetchLeads()}
        smbId={selectedSMBId}
        customStatuses={customStatuses}
        setCustomStatuses={setCustomStatuses}
      />
      <AddCustomLabelDialog
        open={addCustomLabelOpen}
        onClose={() => {
          setAddCustomLabelOpen(false)
          setStatusUpdateTargetLeadId(null)
        }}
        onComplete={handleCustomLabelComplete}
        smbId={selectedSMBId}
        customStatuses={customStatuses}
      />
      <ManageLeadColumnsDialog
        open={manageColumnsOpen}
        onClose={() => setManageColumnsOpen(false)}
        resolvedColumns={resolvedColumns}
        customColumns={customColumns}
        onSaveTableConfig={saveTableConfig}
        onCreateCustomColumn={createCustomColumn}
        onPatchCustomColumn={patchCustomColumn}
        onDeactivateCustomColumn={deactivateCustomColumn}
        initialTab={manageColumnsTab}
      />
      <ConversationDetailDialog
        open={conversationDialogOpen}
        onClose={handleCloseConversationDialog}
        conversationId={selectedConversationId}
        smbId={selectedSMBId}
      />
      {renderEditLeadDialog()}
    </Box>
  )
}

export default Leads
